import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ApiService } from './api.service';
import { jobStatusClass, jobStatusTextClass } from './job-status-style';

@Component({
  selector: 'app-admin-dispatch-allocated',
  standalone: true,
  imports: [CommonModule, RouterLink, GoogleMapsModule],
  template: `
    <!-- Template remains mostly identical using signals and direct bindings -->
    <div class="min-h-full admin-premium-bg">
      <div class="max-w-[1700px] mx-auto p-5">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 shrink-0">
        <div>
          <h1 class="text-xl font-semibold text-slate-950 tracking-tight">{{ statusFilter() || 'Allocated / In Progress' }}</h1>
          <p class="text-slate-500 text-xs">{{ statusFilter() ? 'Filtered allocated jobs requiring attention.' : 'Live view of active jobs and job site locations.' }}</p>
        </div>
        <div class="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <button type="button" (click)="setViewMode('list')" class="h-8 px-3 rounded-lg text-xs font-semibold"
                  [class.bg-[#1A6FA6]]="viewMode === 'list'" [class.text-white]="viewMode === 'list'" [class.text-slate-600]="viewMode !== 'list'">
            List
          </button>
          <button type="button" (click)="setViewMode('map')" class="h-8 px-3 rounded-lg text-xs font-semibold"
                  [class.bg-[#1A6FA6]]="viewMode === 'map'" [class.text-white]="viewMode === 'map'" [class.text-slate-600]="viewMode !== 'map'">
            Map
          </button>
        </div>
      </div>
      <div class="grid grid-cols-2 lg:grid-cols-6 gap-2.5 mb-3">
        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Active</p><p class="text-lg font-semibold text-slate-900 mt-0.5">{{ jobs().length }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 flex items-center justify-center"><i class="ti ti-list-check"></i></span>
        </div>
        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Scheduled</p><p class="text-lg font-semibold text-amber-700 mt-0.5">{{ countAllByStatus('Scheduled') }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center"><i class="ti ti-calendar-check"></i></span>
        </div>
        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">In Progress</p><p class="text-lg font-semibold text-emerald-700 mt-0.5">{{ countAllByStatus('In Progress') }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center"><i class="ti ti-progress-check"></i></span>
        </div>
        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Paused</p><p class="text-lg font-semibold text-orange-700 mt-0.5">{{ countAllByStatus('Paused') }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 text-orange-600 flex items-center justify-center"><i class="ti ti-player-pause"></i></span>
        </div>
        <button type="button" (click)="setStatusFilter('Quote Sent')" class="text-left bg-white rounded-xl border border-cyan-200 shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3 hover:bg-cyan-50/50">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-cyan-600">Quote Sent</p><p class="text-lg font-semibold text-cyan-700 mt-0.5">{{ countAllByStatus('Quote Sent') }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-cyan-50 border border-cyan-100 text-cyan-600 flex items-center justify-center"><i class="ti ti-file-dollar"></i></span>
        </button>
        <div class="bg-white rounded-xl border border-rose-200 shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
          <div><p class="text-[10px] uppercase tracking-widest font-bold text-rose-400">Safety Declined</p><p class="text-lg font-semibold text-rose-700 mt-0.5">{{ safetyDeclinedCount() }}</p></div>
          <span class="w-8 h-8 rounded-lg bg-rose-50 border border-rose-100 text-rose-600 flex items-center justify-center"><i class="ti ti-shield-x"></i></span>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-1.5 mb-4">
        @for (filter of statusFilters; track filter.value) {
          <button type="button" (click)="setStatusFilter(filter.value)" class="h-8 px-3 rounded-lg border text-[11px] font-semibold"
                  [ngClass]="statusFilter() === filter.value ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-200'">
            {{ filter.label }}
          </button>
        }
      </div>

      @if (!selectedJob() && viewMode === 'map') {
        <section class="bg-white rounded-2xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          @if (!mapsApiKey()) {
            <div class="p-10 text-center">
              <i class="ti ti-map-off text-4xl text-slate-300"></i>
              <h2 class="text-lg font-semibold text-slate-900 mt-3">Google Maps key required</h2>
              <p class="text-sm text-slate-500 mt-1">Add the browser API key in Company Settings to enable the live dispatch map.</p>
            </div>
          } @else if (!apiLoaded()) {
            <div class="p-10 text-center text-sm font-semibold text-slate-500">
              <i class="ti ti-loader animate-spin mr-2"></i> Loading map...
            </div>
          } @else {
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <h2 class="text-sm font-bold text-slate-900">Jobs map</h2>
                <p class="text-[11px] text-slate-500 mt-0.5">All jobs with saved site coordinates, across every dispatch status.</p>
              </div>
              <span class="px-2 py-1 rounded-md bg-sky-50 text-[#1A6FA6] text-[10px] font-bold">{{ jobsWithCoordinates().length }} pinned</span>
            </div>
            <google-map height="480px" width="100%" [center]="jobsMapCenter()" [zoom]="jobsMapZoom()" [options]="mapOptions">
              @for (job of jobsWithCoordinates(); track job.id) {
                <map-marker #jobMarker="mapMarker" [position]="jobMarkerPosition(job)" [title]="jobMarkerTitle(job)" [options]="jobMarkerOptions(job)" (mapClick)="openJobPin(jobMarker, job)"></map-marker>
              }
              <map-info-window #jobInfoWindow="mapInfoWindow">
                @if (selectedPin(); as pin) {
                  <div class="min-w-[240px] max-w-[300px] text-[12px] text-slate-700">
                    <p class="font-bold text-slate-950">{{ pin.title }}</p>
                    <p class="mt-1 text-slate-500">{{ pin.subtitle }}</p>
                    <div class="mt-2 flex flex-wrap gap-1">
                      @for (badge of pin.badges || []; track badge) {
                        <span class="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-600">{{ badge }}</span>
                      }
                    </div>
                    @if (pin.detail) {
                      <p class="mt-2 text-slate-600">{{ pin.detail }}</p>
                    }
                  </div>
                }
              </map-info-window>
            </google-map>
            <div class="px-4 py-2 border-t border-slate-100 bg-white flex flex-wrap gap-2">
              @for (item of jobLegend; track item.label) {
                <span class="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                  <span class="w-2.5 h-2.5 rounded-full" [style.background]="item.color"></span>{{ item.label }}
                </span>
              }
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4 bg-slate-50 border-t border-slate-100">
              @for (job of jobsWithCoordinates(); track job.id) {
                <a [routerLink]="['/admin/dispatch/job', job.id]" class="rounded-xl border border-slate-200 bg-white p-3 hover:border-sky-200 hover:bg-sky-50/30 transition-colors">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-slate-900 truncate">#{{ job.id }} {{ job.customer }}</p>
                      <p class="text-xs text-slate-500 truncate">{{ job.address }}</p>
                    </div>
                    <span class="px-2 py-1 rounded-md border text-[10px] font-bold uppercase" [ngClass]="statusClass(job.status)">{{ job.status }}</span>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-2 truncate">{{ job.plumber || 'Unassigned' }}</p>
                </a>
              } @empty {
                <div class="md:col-span-2 xl:col-span-3 text-center text-sm text-slate-400 py-8">No allocated jobs have saved coordinates yet.</div>
              }
            </div>
          }
        </section>
      } @else if (!selectedJob()) {
        <div class="overflow-y-auto">
        <div class="jobs-grid">
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-3xl shadow-sm p-6 border border-slate-100 animate-pulse">
                <div class="flex items-center gap-2 mb-4">
                  <div class="h-5 w-20 bg-slate-200 rounded-full"></div>
                  <div class="h-4 w-10 bg-slate-100 rounded-full"></div>
                </div>
                <div class="h-5 w-3/4 bg-slate-200 rounded-xl mb-2"></div>
                <div class="h-4 w-1/2 bg-slate-100 rounded-xl mb-4"></div>
                <div class="bg-slate-100 rounded-2xl p-4 h-16 mb-4"></div>
                <div class="h-4 w-1/3 bg-slate-100 rounded-xl mb-2"></div>
                <div class="flex justify-end pt-4 border-t border-slate-100">
                  <div class="h-8 w-28 bg-slate-200 rounded-xl"></div>
                </div>
              </div>
            }
          } @else if (jobs().length === 0) {
            <div class="col-span-full bg-white rounded-3xl shadow-sm p-16 flex flex-col items-center justify-center text-center border border-slate-100">
              <div class="w-24 h-24 rounded-full flex items-center justify-center mb-6" style="background:linear-gradient(135deg, rgba(43,154,210,0.1), rgba(94,201,240,0.1));">
                <i class="ti ti-truck text-5xl" style="color:#2B9AD2;"></i>
              </div>
              <h3 class="text-2xl font-black text-slate-900 mb-2">No Active Allocations</h3>
              <p class="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">There are currently no jobs actively assigned or in-progress by the fleet.</p>
            </div>
          } @else {
            @for (job of jobs(); track job.id) {
              <div class="job-card" [ngClass]="{ 'border-rose-300 ring-2 ring-rose-100 bg-rose-50/30': isSafetyDeclined(job) }">
                  <div class="card-header">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="job-status" [ngClass]="statusClass(job.status)">
                            {{job.status | uppercase}}
                        </span>
                        @if (isSafetyDeclined(job)) {
                          <span class="job-status status-error">
                            SAFETY DECLINED
                          </span>
                        }
                      </div>
                      <span class="text-xs font-bold text-slate-400">#{{job.id}}</span>
                  </div>

                  <div class="card-body">
                      <h3>{{job.customer}}</h3>
                      <p class="job-date"><i class="ti ti-map-pin"></i> {{job.address}}</p>
                      
                      <div class="bg-slate-50 p-3 rounded-lg border border-slate-100 mt-2">
                          <p class="text-xs text-slate-600 line-clamp-3">{{job.desc}}</p>
                      </div>

                      <div class="mt-3 text-xs text-slate-500 font-bold flex items-center gap-1">
                          <i class="ti ti-user text-[#2B9AD2]"></i> {{job.plumber}}
                      </div>
                      <div class="mt-2 text-[10px] font-bold uppercase tracking-widest" [ngClass]="stageTextClass(job.status)">
                          {{ stageLabel(job.status) }}
                      </div>
                      @if (job.status === 'Paused') {
                        <div class="mt-1 text-[10px] font-semibold text-orange-600">
                          Paused since {{ formatDate(job.pauseStartedAt || job.pausedAt) }}
                        </div>
                      }
                      @if (isSafetyDeclined(job)) {
                        <div class="mt-2 rounded-lg bg-rose-100 border border-rose-200 text-rose-800 px-2 py-1 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                          <i class="ti ti-shield-x"></i> Safety declined
                        </div>
                      }
                  </div>

                  <div class="card-footer">
                      <a [routerLink]="['/admin/dispatch/job', job.id]" class="btn-open text-center block" style="text-decoration: none; display: block; box-sizing: border-box;">
                          View Details →
                      </a>
                  </div>
              </div>
            }
          }
        </div>
        </div>
      } @else {
        @if (selectedJob(); as job) {
          <div class="max-w-4xl mx-auto space-y-5">
            <button (click)="selectJob(null)" class="flex items-center gap-1.5 text-slate-600 font-bold text-sm mb-2 hover:text-[#2B9AD2] transition-colors cursor-pointer"><i class="ti ti-arrow-left"></i> Back to Allocated Jobs</button>
            <div class="bg-white rounded-3xl shadow-sm p-6 flex justify-between items-start border border-slate-100">
              <div><span class="text-[11px] font-bold text-slate-400">#{{job.id}}</span><h2 class="text-2xl font-black text-slate-900 mt-1">{{job.customer}}</h2><p class="text-sm text-slate-500 mt-1.5"><i class="ti ti-map-pin"></i> {{job.address}}</p></div>
            </div>
          </div>
        }
      }
      </div>
    </div>
  `
})
export class AdminDispatchAllocatedComponent implements OnInit {
  @ViewChild('jobInfoWindow') jobInfoWindow?: MapInfoWindow;
  allJobs = signal<any[]>([]);
  jobs = signal<any[]>([]);
  isLoading = signal(true);
  skeletons = [1, 2, 3, 4, 5, 6];
  viewMode: 'list' | 'map' = 'list';
  apiLoaded = signal(false);
  mapsApiKey = signal('');
  selectedPin = signal<any | null>(null);
  mapZoom = 11;
  mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true
  };
  selectedJob = signal<any | null>(null);
  statusFilter = signal('');
  statusFilters = [
    { label: 'All active', value: '' },
    { label: 'Scheduled', value: 'Scheduled' },
    { label: 'In progress', value: 'In Progress' },
    { label: 'Paused', value: 'Paused' },
    { label: 'Quote sent', value: 'Quote Sent' },
    { label: 'Approved', value: 'Approved' }
  ];
  jobLegend = [
    { label: 'Urgent', color: '#dc2626' },
    { label: 'In progress', color: '#059669' },
    { label: 'Paused', color: '#ea580c' },
    { label: 'Scheduled', color: '#2563eb' },
    { label: 'Approved', color: '#7c3aed' },
    { label: 'Quote sent', color: '#0891b2' }
  ];

  private api = inject(ApiService);
  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      this.statusFilter.set(params.get('status') || '');
      this.applyStatusFilter();
    });
    this.loadJobs();
    this.loadMapSettings();
  }

  loadJobs() {
    this.isLoading.set(true);
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.jobs) {
          const uniqueJobsMap = new Map();
          
          res.data.jobs
            .forEach((j: any) => {
               const jobId = j.job_id || j.id;
               if (!uniqueJobsMap.has(jobId)) {
                   uniqueJobsMap.set(jobId, {
                      id: jobId,
                      status: j.status,
                      customer: j.customer_name || 'Anonymous',
                      address: j.address || 'Unknown Address',
                      latitude: j.latitude ?? null,
                      longitude: j.longitude ?? null,
                      desc: j.description,
                      pausedAt: j.paused_at || null,
                      pauseStartedAt: j.pause_started_at || null,
                      latestSafetyStatus: j.latest_safety_status || null,
                      plumbers: new Set()
                   });
               }
               const existing = uniqueJobsMap.get(jobId);
               
               if (j.assigned_workers) {
                   j.assigned_workers.split(',').forEach((w: string) => existing.plumbers.add(w.trim()));
               } else if (j.worker_name || j.plumber_name) {
                   existing.plumbers.add(j.worker_name || j.plumber_name);
               }
            });

          const mappedJobs = Array.from(uniqueJobsMap.values()).map(job => {
            return {
              ...job,
              plumber: job.plumbers.size > 0 ? Array.from(job.plumbers).filter(Boolean).join(', ') : 'Unassigned'
            };
          });
          this.allJobs.set(mappedJobs);
          this.applyStatusFilter();
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  setViewMode(mode: 'list' | 'map') {
    this.viewMode = mode;
    this.selectedPin.set(null);
  }

  setStatusFilter(status: string) {
    this.statusFilter.set(status);
    this.selectedJob.set(null);
    this.applyStatusFilter();
  }

  private applyStatusFilter() {
    const activeStatuses = ['Scheduled', 'In Progress', 'Paused', 'Quote Sent', 'Approved'];
    const status = this.statusFilter();
    this.jobs.set(this.allJobs().filter((job: any) => activeStatuses.includes(job.status) && (!status || job.status === status)));
  }
  selectJob(job: any) { this.selectedJob.set(job); }

  loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsApiKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  jobsMapCenter(): google.maps.LatLngLiteral {
    const firstJob = this.jobsWithCoordinates()[0];
    return firstJob ? this.jobMarkerPosition(firstJob) : { lat: -36.8485, lng: 174.7633 };
  }

  jobsMapZoom(): number {
    return this.jobsWithCoordinates().length > 1 ? 10 : 12;
  }

  jobsWithCoordinates(): any[] {
    return this.jobs().filter((job: any) => this.hasCoordinates(job));
  }

  jobMarkerPosition(job: any): google.maps.LatLngLiteral {
    return { lat: Number(job.latitude), lng: Number(job.longitude) };
  }

  jobMarkerTitle(job: any): string {
    return `${job.id} - ${job.customer || 'Job'} - ${job.status || ''}`;
  }

  jobMarkerOptions(job: any): google.maps.MarkerOptions {
    const color = this.jobMarkerColor(job);
    return {
      icon: this.mapPinIcon(color),
      label: {
        text: this.jobMarkerLabel(job.status),
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '11px'
      }
    };
  }

  openJobPin(marker: MapMarker, job: any) {
    this.selectedPin.set({
      title: `#${job.id} ${job.customer || 'Job'}`,
      subtitle: job.address || 'No address recorded',
      detail: job.plumber && job.plumber !== 'Unassigned' ? `Assigned to ${job.plumber}` : 'No assigned worker shown',
      badges: [this.jobMarkerTag(job), job.status || 'Job', job.priority || '', job.type || ''].filter(Boolean)
    });
    this.jobInfoWindow?.open(marker);
  }

  hasCoordinates(item: any): boolean {
    return item?.latitude !== null
      && item?.latitude !== undefined
      && item?.latitude !== ''
      && item?.longitude !== null
      && item?.longitude !== undefined
      && item?.longitude !== ''
      && Number.isFinite(Number(item.latitude))
      && Number.isFinite(Number(item.longitude));
  }

  private jobMarkerLabel(status: string): string {
    if (status === 'In Progress') return 'IP';
    if (status === 'Paused') return 'P';
    if (status === 'Scheduled') return 'S';
    if (status === 'Approved') return 'A';
    if (status === 'Quote Sent') return 'Q';
    return 'J';
  }

  private jobMarkerColor(job: any): string {
    if (job?.priority === 'Urgent') return '#dc2626';
    if (job?.status === 'In Progress') return '#059669';
    if (job?.status === 'Paused') return '#ea580c';
    if (job?.status === 'Scheduled') return '#2563eb';
    if (job?.status === 'Approved') return '#7c3aed';
    if (job?.status === 'Quote Sent') return '#0891b2';
    if (job?.status === 'Ready for Office') return '#0f766e';
    if (job?.status === 'Completed') return '#64748b';
    if (job?.status === 'Terminated') return '#991b1b';
    return '#475569';
  }

  private jobMarkerTag(job: any): string {
    if (job?.priority === 'Urgent') return 'Red: urgent';
    if (job?.status === 'In Progress') return 'Green: in progress';
    if (job?.status === 'Paused') return 'Orange: paused';
    if (job?.status === 'Scheduled') return 'Blue: scheduled';
    if (job?.status === 'Approved') return 'Purple: approved';
    if (job?.status === 'Quote Sent') return 'Cyan: quote sent';
    if (job?.status === 'Ready for Office') return 'Teal: ready office';
    if (job?.status === 'Completed') return 'Grey: completed';
    if (job?.status === 'Terminated') return 'Dark red: terminated';
    return 'Slate: job';
  }

  private mapPinIcon(color: string): google.maps.Icon {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="50" viewBox="0 0 42 50"><path fill="${color}" stroke="#ffffff" stroke-width="2" d="M21 2C11.1 2 3 10.1 3 20c0 13.5 18 28 18 28s18-14.5 18-28C39 10.1 30.9 2 21 2Z"/><circle cx="21" cy="20" r="9" fill="rgba(255,255,255,0.22)"/></svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(34, 40),
      labelOrigin: new google.maps.Point(17, 18),
      anchor: new google.maps.Point(17, 40)
    };
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps) {
      this.apiLoaded.set(true);
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => this.apiLoaded.set(true));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => this.apiLoaded.set(true);
    document.head.appendChild(script);
  }

  countByStatus(status: string): number {
    return this.jobs().filter(job => job.status === status).length;
  }

  countAllByStatus(status: string): number {
    return this.allJobs().filter(job => job.status === status).length;
  }

  safetyDeclinedCount(): number {
    return this.jobs().filter(job => this.isSafetyDeclined(job)).length;
  }

  isSafetyDeclined(job: any): boolean {
    return job?.latestSafetyStatus === 'Declined';
  }

  quoteStageCount(): number {
    return this.jobs().filter(job => ['Quote Sent', 'Approved'].includes(job.status)).length;
  }

  statusClass(status: string): string {
    return jobStatusClass(status);
  }

  stageTextClass(status: string): string {
    return jobStatusTextClass(status);
  }

  stageLabel(status: string): string {
    if (status === 'Quote Sent') return 'Waiting on customer approval';
    if (status === 'Approved') return 'Approved - schedule worker attendance';
    if (status === 'In Progress') return 'Field work in progress';
    if (status === 'Paused') return 'Paused by field worker';
    return 'Scheduled with field team';
  }

  formatDate(value: string | null): string {
    if (!value) return 'not recorded';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'not recorded';
    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}




